/**
 * Hook para Gerenciar Estado de Onboarding
 * 
 * DESCRIÇÃO:
 * Hook que verifica se o usuário completou o processo de onboarding
 * inicial e fornece funções para gerenciar este estado.
 * 
 * FUNCIONALIDADES:
 * - Verificar se onboarding foi completado
 * - Buscar dados do perfil do usuário
 * - Controlar exibição do modal de onboarding
 * - Revalidar dados após completar onboarding
 * 
 * CONECTA COM:
 * - Tabela user_profiles
 * - AuthContext para dados do usuário
 * - OnboardingModal
 */

import { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

interface UserProfile {
  id: string;
  user_id: string;
  onboarding_completed: boolean;
  privacy_policy_accepted_at: string | null;
  nome_completo: string | null;
  profile_type: string;
  clinica_id: string | null;
}

export const useOnboarding = () => {
  const { user, loading: authLoading } = useAuth();
  const queryClient = useQueryClient();
  const [showOnboardingModal, setShowOnboardingModal] = useState(false);

  // Buscar dados do perfil do usuário
  const { data: profile, isLoading, error } = useQuery<UserProfile>({
    queryKey: ['user-profile', user?.id],
    queryFn: async () => {
      if (!user?.id) throw new Error('Usuário não autenticado');

      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (error) throw error;
      return data;
    },
    enabled: !!user?.id && !authLoading,
    staleTime: 1000 * 60 * 5, // 5 minutos
  });

  // Verificar se precisa mostrar o modal de onboarding
  useEffect(() => {
    if (profile && !authLoading) {
      // Mostrar modal se onboarding não foi completado
      const needsOnboarding = !profile.onboarding_completed;
      setShowOnboardingModal(needsOnboarding);
    }
  }, [profile, authLoading]);

  // Função para marcar onboarding como completo
  const completeOnboarding = () => {
    setShowOnboardingModal(false);
    // Invalidar cache do perfil para refetch
    queryClient.invalidateQueries({ queryKey: ['user-profile'] });
  };

  // Função para forçar verificação do onboarding (útil para debug)
  const checkOnboardingStatus = () => {
    queryClient.invalidateQueries({ queryKey: ['user-profile'] });
  };

  return {
    // Estados
    isLoading: authLoading || isLoading,
    needsOnboarding: showOnboardingModal,
    profile,
    error,

    // Flags de status
    isOnboardingCompleted: profile?.onboarding_completed ?? false,
    hasAcceptedPrivacyPolicy: !!profile?.privacy_policy_accepted_at,

    // Funções
    completeOnboarding,
    checkOnboardingStatus,
  };
};